import { useAppDispatch, useAppSelector } from '@dealstack/frontend/app/hooks/redux';
import { useProgrammeUsers } from '@dealstack/frontend/app/pages/Project/MIP/hooks';
import { useMIPManageContext } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/MIPManageContext';
import {
	addProgrammePermissionToUser,
	getProgrammeUserRequest,
	removeProgrammePermissionFromUser,
	removeProgrammeUserRequest,
	updateIsContactPersonRequest,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/MipManage.api';
import { selectSelectedProgrammeMemberId } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/MipManage.selectors';
import { setSelectedProgrammeMemberId } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/MipManage.slice';
import { ProgrammeSettingsView } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/MipManage.types';
import { AutomatedEmails } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/AutomatedEmails/AutomatedEmails.tsx';
import { useDealstackFeatureFlag } from '@dealstack/frontend/shared/hooks/useDealstackFeatureFlag/useDealstackFeatureFlag.hook.ts';
import { FEATURE_FLAG } from '@dealstack/shared/lib/common/featureFlag';
import { Classifiers } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/Classifiers/Classifiers.tsx';
import { ProgrammeEmailTemplates } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/EmailTemplates/ProgrammeEmailTemplates';
import { ProgrammeEmailTemplateDetailPage } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/EmailTemplates/ProgrammeEmailTemplateDetailPage';
import { useEmailTemplate } from '@dealstack/frontend/shared/components/EmailTemplates/hooks/useEmailTemplates';
import { EmailTemplateScopeKind } from '@dealstack/frontend/shared/components/EmailTemplates/EmailTemplates.types';
import { InvestorData } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/InvestorData/InvestorData.tsx';
import { KycSettings } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/KycSettings/KycSettings.tsx';
import { Loans } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/Loans/Loans';
import { MasterDocuments } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/MasterDocuments.tsx';
import { ProgrammeGeneralSettings } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/ProgrammeGeneralSettings/ProgrammeGeneralSettings';
import { ProgrammeSettingsSidebar } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/ProgrammeSettingsSidebar';
import { ProgrammeUsers } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/ProgrammeUsers/ProgrammeUsers';
import { Securities } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/Securities/Securities.tsx';
import { StyledProgrammeSettingsContainer } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/styles.ts';
import { TaxAndCompliance } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/TaxAndCompliance/TaxAndCompliance.tsx';
import {
	PROGRAMME_SETTINGS_ROUTES,
	MIP_MANAGE_ROUTES,
} from '@dealstack/frontend/app/pages/Routing/routes.ts';
import { mipManagePath } from '@dealstack/frontend/app/pages/Routing/routes.utils.ts';
import { selectUserId } from '@dealstack/frontend/app/redux/user.selectors';
import { useBreadcrumbs } from '@dealstack/frontend/shared/components/Breadcrumbs/useBreadcrumbs.tsx';
import { UserManagement } from '@dealstack/frontend/shared/components/UserManagement/UserManagement';
import { transformPermissions } from '@dealstack/frontend/shared/components/UserPermissions/userPermissions.utils';
import { setPermissionsForResourceType } from '@dealstack/frontend/shared/dataExchange/services/permission.api';
import { DashboardGrid } from '@dealstack/frontend/shared/layout/DashboardGrid/DashboardGrid.tsx';
import { useTranslationMipCreate } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import {
	useTranslationAdminDashboard,
	useTranslationCommonUi,
	useTranslationMipManage,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import {
	getResourceTypePermissionsWithOwner,
	PermissionWithOwner,
	ResourceType,
} from '@dealstack/shared/lib/common/fineGrainedAuthorization/permissions';
import { PermissionRole } from '@dealstack/shared/lib/generated';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { Route, Routes, useNavigate, useParams } from 'react-router-dom';
import { AddBreadcrumbItem } from '@dealstack/frontend/shared/components/Breadcrumbs/useBreadcrumbs.types';
import { useMipManageBreadcrumbsPrefix } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/utils/mipManageBreadcrumbs.utils';
import { useMipProgrammeEntities } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/hooks/useMipProgrammeEntities';

const ProgrammeSettingsContent = () => {
	const dispatch = useAppDispatch();
	const tMipManage = useTranslationMipManage();
	const tCommonUi = useTranslationCommonUi();
	const tAdminDashboard = useTranslationAdminDashboard();
	const navigate = useNavigate();
	const { value: automatedRemindersEnabled } = useDealstackFeatureFlag(
		FEATURE_FLAG.AUTOMATED_EMAIL_REMINDERS
	);
	const { mipId: programmeId, '*': currentPath } = useParams();
	const selectedProgrammeMemberId = useAppSelector(selectSelectedProgrammeMemberId);
	const currentUserId = useAppSelector(selectUserId);
	const mipManageController = useMIPManageContext();
	const settingsPath = mipManagePath(MIP_MANAGE_ROUTES.SETTINGS, {
		dealId: mipManageController?.dealId ?? '',
		mipId: programmeId ?? '',
	});
	const settingsViewPath = (subPath: string) =>
		subPath ? `${settingsPath}/${subPath}` : settingsPath;
	const programmeName = mipManageController?.mipData?.programmeName || 'Programme';
	const { data: programmeEntities = [], isFetched: hasFetchedProgrammeEntities } =
		useMipProgrammeEntities(programmeId);

	const tMipCreate = useTranslationMipCreate();

	const VIEW_CONFIG = useMemo(
		() => ({
			[ProgrammeSettingsView.General]: {
				viewName: 'Overview',
				path: '',
			},
			[ProgrammeSettingsView.Members]: {
				viewName: tMipManage('programme_settings.labels.members'),
				path: ProgrammeSettingsView.Members,
			},
			[ProgrammeSettingsView.Securities]: {
				viewName: tMipManage('programme_settings.labels.securities'),
				path: ProgrammeSettingsView.Securities,
			},
			[ProgrammeSettingsView.Loans]: {
				viewName: tMipManage('programme_settings.labels.loans'),
				path: ProgrammeSettingsView.Loans,
			},
			[ProgrammeSettingsView.MasterDocuments]: {
				viewName: tMipManage('programme_settings.labels.documents'),
				path: ProgrammeSettingsView.MasterDocuments,
			},
			[ProgrammeSettingsView.InvestorData]: {
				viewName: tMipManage('programme_settings.labels.investor_data'),
				path: ProgrammeSettingsView.InvestorData,
			},
			[ProgrammeSettingsView.KycSettings]: {
				viewName: tMipManage('programme_settings.labels.kyc'),
				path: ProgrammeSettingsView.KycSettings,
			},
			[ProgrammeSettingsView.Tax]: {
				viewName: tMipManage('programme_settings.labels.tax'),
				path: ProgrammeSettingsView.Tax,
			},
			[ProgrammeSettingsView.Classifiers]: {
				viewName: tMipManage('programme_settings.labels.classifiers'),
				path: ProgrammeSettingsView.Classifiers,
			},
			[ProgrammeSettingsView.EmailTemplates]: {
				viewName: 'Email templates',
				path: ProgrammeSettingsView.EmailTemplates,
			},
			[ProgrammeSettingsView.AutomatedEmails]: {
				viewName: tMipManage('programme_settings.labels.automated_emails'),
				path: ProgrammeSettingsView.AutomatedEmails,
			},
		}),
		[tMipManage]
	);

	const selectedEmailTemplateType = useMemo(() => {
		const match = currentPath?.match(
			new RegExp(`^${ProgrammeSettingsView.EmailTemplates}/([^/]+)$`)
		);
		return match?.[1] ?? '';
	}, [currentPath]);

	const { data: selectedEmailTemplate } = useEmailTemplate({
		scope: { kind: EmailTemplateScopeKind.PROGRAMME, id: programmeId ?? '' },
		type: selectedEmailTemplateType,
		enabled: Boolean(programmeId && selectedEmailTemplateType),
	});

	const currentView = useMemo(() => {
		if (!currentPath) {
			return ProgrammeSettingsView.General;
		}

		if (currentPath.startsWith(`${ProgrammeSettingsView.User}/`)) {
			return ProgrammeSettingsView.User;
		}

		return (
			Object.values(ProgrammeSettingsView).find((view) => currentPath.startsWith(view)) ??
			ProgrammeSettingsView.General
		);
	}, [currentPath]);

	const selectedSecurityEntityId = useMemo(() => {
		const securitiesPathPrefix = `${ProgrammeSettingsView.Securities}/`;

		if (!currentPath?.startsWith(securitiesPathPrefix)) {
			return undefined;
		}

		return currentPath.replace(securitiesPathPrefix, '').split('/')[0] || undefined;
	}, [currentPath]);

	const resolvedSecurityEntityId = useMemo(() => {
		if (
			selectedSecurityEntityId &&
			programmeEntities.some((entity) => entity.entityId === selectedSecurityEntityId)
		) {
			return selectedSecurityEntityId;
		}

		if (programmeEntities.length > 0) {
			return programmeEntities[0].entityId;
		}

		if (!hasFetchedProgrammeEntities) {
			return selectedSecurityEntityId;
		}

		return mipManageController?.mipData?.entityId;
	}, [
		hasFetchedProgrammeEntities,
		mipManageController?.mipData?.entityId,
		programmeEntities,
		selectedSecurityEntityId,
	]);

	const { refetch: refetchProgrammeUsers } = useProgrammeUsers({
		programmeId: programmeId!,
	});

	const { mutate: removeUserMutation, isPending: isRemovingUser } = useMutation({
		mutationFn: removeProgrammeUserRequest,
	});

	const removeUser = (userId: string) => {
		if (!programmeId) {
			return;
		}

		removeUserMutation(
			{ userId, programmeId },
			{
				onSuccess: async () => {
					showToastV2({
						message: tAdminDashboard(
							'programme_settings.member.toast.remove_user.success_message'
						),
					});
					navigate(settingsViewPath(ProgrammeSettingsView.Members));
					await refetchProgrammeUsers();
				},
			}
		);
	};

	const { mutate: updateIsContactPersonMutation, isPending: isUpdatingIsContactPerson } =
		useMutation({
			mutationFn: updateIsContactPersonRequest,
		});

	const updateIsContactPerson = ({
		userId,
		isContactPerson,
	}: {
		userId: string;
		isContactPerson: boolean;
	}) => {
		if (!programmeId) {
			return;
		}

		updateIsContactPersonMutation(
			{ userId, programmeId, isContactPerson },
			{
				onSuccess: async () => {
					showToastV2({
						message: tAdminDashboard(
							'toast.update_user_contact_person.success_message'
						),
					});
					await refetchUser();
					// Need to reload users to check if there is more than one contact person now
					await refetchProgrammeUsers();
				},
			}
		);
	};

	const {
		data: user,
		isFetching: isFetchingUser,
		refetch: refetchUser,
	} = useQuery({
		queryKey: [
			API_PATHS.MIP_PROGRAMMEID_USERS_USERID_GET,
			{ userId: selectedProgrammeMemberId!, programmeId: programmeId! },
		],
		queryFn: () =>
			getProgrammeUserRequest(API_PATHS.MIP_PROGRAMMEID_USERS_USERID_GET, {
				userId: selectedProgrammeMemberId!,
				programmeId: programmeId!,
			}),
		enabled: !!selectedProgrammeMemberId && !!programmeId,
		select: (user) => {
			const programmePermissions = getResourceTypePermissionsWithOwner(
				ResourceType.PROGRAMME
			);
			const resourcePermissions = programmePermissions.map((permission) => {
				return {
					label: tMipCreate(`invite_team_member.relation.${permission}`),
					value: permission,
					description: tMipCreate(
						`invite_team_member.relation.description.${permission}`
					),
				};
			});
			const isCurrentUser = selectedProgrammeMemberId === currentUserId;
			const permissions = transformPermissions({
				resourcePermissions,
				userPermissions: user.permissions as unknown as PermissionWithOwner[],
				disablePermissionForCurrentUser: isCurrentUser,
				resourceType: ResourceType.PROGRAMME,
			});
			return {
				...user,
				permissions,
				isContactPerson: user.isContactPerson,
			};
		},
	});

	const {
		mutate: addProgrammePermissionToUserMutation,
		isPending: isAddingProgrammePermissionToUser,
	} = useMutation({
		mutationFn: addProgrammePermissionToUser,
	});
	const {
		mutate: removeProgrammePermissionFromUserMutation,
		isPending: isRemovingProgrammePermissionFromUser,
	} = useMutation({
		mutationFn: removeProgrammePermissionFromUser,
	});
	const { mutate: setProgrammePermissionToUserMutation, isPending: isSetDealPermissionToUser } =
		useMutation({
			mutationFn: (variables: Parameters<typeof setPermissionsForResourceType>[0]) =>
				setPermissionsForResourceType(variables),
		});

	const updatePermission = (permissionRole: PermissionRole, removePermission: boolean) => {
		const explicitPermissions = user?.permissions
			// Must check for false and not undefined
			.filter((p) => p.isImplicit === false)
			.map((p) => p.value);

		const onSuccess = async () => {
			showToastV2({
				message: tAdminDashboard('toast.update_user_permission.success_message'),
			});
			await refetchUser();
			await refetchProgrammeUsers();
		};

		// Removing the last explicit Permission
		if (explicitPermissions?.length === 1 && removePermission) {
			return setProgrammePermissionToUserMutation(
				{
					body: {
						userId: selectedProgrammeMemberId!,
						permissionRoles: [PermissionRole.VIEWER],
					},
					params: { resourceType: ResourceType.PROGRAMME, resourceId: programmeId! },
				},
				{ onSuccess }
			);
		}

		if (removePermission) {
			return removeProgrammePermissionFromUserMutation(
				{
					programmeId: programmeId!,
					userId: selectedProgrammeMemberId!,
					permissionRoles: [permissionRole],
				},
				{ onSuccess }
			);
		}
		return addProgrammePermissionToUserMutation(
			{
				programmeId: programmeId!,
				userId: selectedProgrammeMemberId!,
				permissionRoles: [permissionRole],
			},
			{ onSuccess }
		);
	};

	const selectUserAndView = (view: ProgrammeSettingsView, userId: string) => {
		dispatch(setSelectedProgrammeMemberId(userId));
		navigate(settingsViewPath(`${ProgrammeSettingsView.User}/${userId}`));
	};

	const handleViewChange = (view: ProgrammeSettingsView) => {
		const config = VIEW_CONFIG[view];
		if (config) {
			navigate(settingsViewPath(config.path));
		}
	};

	const handleSecurityEntityChange = (entityId: string) => {
		navigate(settingsViewPath(`${ProgrammeSettingsView.Securities}/${entityId}`));
	};

	useEffect(() => {
		if (currentView !== ProgrammeSettingsView.Securities || !resolvedSecurityEntityId) {
			return;
		}

		if (selectedSecurityEntityId !== resolvedSecurityEntityId) {
			navigate(
				`${settingsPath}/${ProgrammeSettingsView.Securities}/${resolvedSecurityEntityId}`,
				{
					replace: true,
				}
			);
		}
	}, [currentView, navigate, resolvedSecurityEntityId, selectedSecurityEntityId, settingsPath]);

	const dealId = mipManageController?.dealId;
	const mipId = programmeId;
	const breadcrumbsPrefix = useMipManageBreadcrumbsPrefix({ dealId, mipId, programmeName });

	const breadcrumbsArray = useMemo<AddBreadcrumbItem[]>(() => {
		const baseBreadcrumbs: AddBreadcrumbItem[] = [
			...breadcrumbsPrefix,
			{
				name: tMipManage('programme_settings.labels.programme_settings'),
				url: mipManagePath(MIP_MANAGE_ROUTES.SETTINGS, {
					dealId: dealId ?? '',
					mipId: mipId ?? '',
				}),
				hideInAppHeader: true,
			},
		];

		if (currentView !== ProgrammeSettingsView.General) {
			const config = VIEW_CONFIG[currentView];

			// Handle User view separately as it has dynamic content
			if (currentView === ProgrammeSettingsView.User && user) {
				baseBreadcrumbs.push({
					name: `${user.firstName} ${user.lastName}`,
					url: '',
					noLink: true,
					hideInAppHeader: true,
				} as AddBreadcrumbItem);
			} else if (config) {
				baseBreadcrumbs.push({
					name: config.viewName,
					url: mipManagePath(`${MIP_MANAGE_ROUTES.SETTINGS}/${config.path}`, {
						dealId: dealId ?? '',
						mipId: mipId ?? '',
					}),
					hideInAppHeader: true,
				} as AddBreadcrumbItem);
			}

			if (selectedEmailTemplate) {
				baseBreadcrumbs.push({
					name: selectedEmailTemplate.name,
					url: '',
					noLink: true,
					hideInAppHeader: true,
				} as AddBreadcrumbItem);
			}
		}

		return baseBreadcrumbs;
	}, [
		breadcrumbsPrefix,
		currentView,
		user,
		dealId,
		mipId,
		tMipManage,
		VIEW_CONFIG,
		selectedEmailTemplate,
	]);

	useBreadcrumbs(breadcrumbsArray);

	const pageTitle =
		currentView === ProgrammeSettingsView.User
			? `${user?.firstName} ${user?.lastName}`
			: VIEW_CONFIG[currentView]?.viewName ??
				tMipManage('programme_settings.labels.programme_settings');

	return (
		<StyledProgrammeSettingsContainer>
			<DashboardGrid
				pageTitle={pageTitle}
				hideBreadcrumbs
				sidebarContent={
					<ProgrammeSettingsSidebar
						onViewChange={handleViewChange}
						currentView={currentView}
						programmeEntities={programmeEntities}
						selectedSecurityEntityId={resolvedSecurityEntityId}
						onSecurityEntityChange={handleSecurityEntityChange}
					/>
				}
				mainContent={
					<Routes>
						<Route
							index
							element={<ProgrammeGeneralSettings onViewChange={handleViewChange} />}
						/>
						<Route
							path={PROGRAMME_SETTINGS_ROUTES.MEMBERS}
							element={<ProgrammeUsers onViewChange={selectUserAndView} />}
						/>
						<Route
							path={PROGRAMME_SETTINGS_ROUTES.SECURITIES}
							element={<Securities />}
						/>
						<Route
							path={`${PROGRAMME_SETTINGS_ROUTES.SECURITIES}/:entityId`}
							element={<Securities />}
						/>
						<Route
							path={PROGRAMME_SETTINGS_ROUTES.LOANS}
							element={<Loans mipId={programmeId!} />}
						/>
						<Route
							path={PROGRAMME_SETTINGS_ROUTES.DOCUMENTS}
							element={<MasterDocuments />}
						/>
						<Route
							path={PROGRAMME_SETTINGS_ROUTES.KYC_SETTINGS}
							element={<KycSettings />}
						/>
						<Route
							path={PROGRAMME_SETTINGS_ROUTES.INVESTOR_DATA}
							element={<InvestorData />}
						/>
						<Route
							path={PROGRAMME_SETTINGS_ROUTES.TAX}
							element={<TaxAndCompliance />}
						/>
						<Route
							path={PROGRAMME_SETTINGS_ROUTES.CLASSIFIERS}
							element={<Classifiers />}
						/>
						{automatedRemindersEnabled && (
							<Route
								path={PROGRAMME_SETTINGS_ROUTES.AUTOMATED_EMAILS}
								element={<AutomatedEmails />}
							/>
						)}
						<Route
							path={PROGRAMME_SETTINGS_ROUTES.EMAIL_TEMPLATES}
							element={<ProgrammeEmailTemplates mipId={programmeId!} />}
						/>
						<Route
							path={PROGRAMME_SETTINGS_ROUTES.EMAIL_TEMPLATE_DETAIL}
							element={<ProgrammeEmailTemplateDetailPage mipId={programmeId!} />}
						/>
						<Route
							path={PROGRAMME_SETTINGS_ROUTES.USER}
							element={
								<UserManagement
									title={programmeName}
									deleteFromText={tCommonUi('title.programme')}
									showIsContactPerson
									user={user}
									membersBreadcrumbTitle={tMipManage(
										'root.programme_members.header.title'
									)}
									isContactPerson={user?.isContactPerson ?? false}
									handleUpdateContactPerson={async (isContactPerson: boolean) => {
										updateIsContactPerson({
											userId: selectedProgrammeMemberId!,
											isContactPerson,
										});
									}}
									isLoading={
										isFetchingUser ||
										isRemovingProgrammePermissionFromUser ||
										isAddingProgrammePermissionToUser ||
										isUpdatingIsContactPerson ||
										isSetDealPermissionToUser ||
										isRemovingUser
									}
									handleUpdateUser={() => Promise.resolve()}
									handleRemoveUser={removeUser}
									canRemoveUser={user?.canBeRemoved}
									handlePermissionChange={updatePermission}
									text={{
										permissionTitle: tMipManage(
											'invite_member_drawer.form.role_field.label'
										),
									}}
								/>
							}
						/>
					</Routes>
				}
				leftSidebarLoading={isFetchingUser}
				dataLoading={false}
			/>
		</StyledProgrammeSettingsContainer>
	);
};

export const ProgrammeSettings = () => {
	return <ProgrammeSettingsContent />;
};
